import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink
} from '@/components/ui/pagination'
import { Button } from '@/components/ui/button'
import { ChevronLeftIcon, ChevronRightIcon, ChevronsLeftIcon, ChevronsRightIcon } from 'lucide-react'
import { useDispatch, useSelector } from 'react-redux'
import { ICON_SIZE_1 } from '@/utils/constant'
import { setPageIndex, triggerRefresh } from '@/store/slices/paginationSlice'
import { RootState } from '@/store/store'

interface TablePaginationProps {
    tableName: string
}

export function TablePagination({ tableName }: TablePaginationProps) {
    const dispatch = useDispatch()
    const { totalData, searchParams, pageIndex, pageSize, filterData } = useSelector((state: RootState) => state.pagination[tableName])
    const totalPages = Math.ceil(filterData / pageSize)

    const handlePageChange = (newPage: number) => {
        dispatch(setPageIndex({ table: tableName, pageIndex: newPage }))
        dispatch(triggerRefresh({ table: tableName, isRefresh: true }))
    }

    const showPages = () => {
        const pages = []
        const maxVisiblePages = 3

        pages.push(0)

        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i < totalPages; i++) {
                pages.push(i)
            }
        } else {
            const startPage = Math.max(1, pageIndex - 1)
            const endPage = Math.min(totalPages - 2, pageIndex + 1)

            if (startPage > 1) {
                pages.push(-1)
            }

            for (let i = startPage; i <= endPage; i++) {
                pages.push(i)
            }

            if (endPage < totalPages - 2) {
                pages.push(-2)
            }

            if (totalPages > 1) {
                pages.push(totalPages - 1)
            }
        }

        return pages
    }

    return (
        <>
            <div className="hidden flex-1 text-sm text-muted-foreground lg:flex">
                {searchParams?.search ? (
                    <>
                        Showing {pageIndex * pageSize + 1}{' '}
                        to {Math.min((pageIndex + 1) * pageSize, Number(filterData))}{' '}
                        of {filterData} entries (filtered from {Number(totalData)} total entries)
                    </>
                ) : (
                    <>
                        Showing {pageIndex * pageSize + 1}{' '}
                        to {Math.min((pageIndex + 1) * pageSize, Number(totalData))}{' '}
                        of {Number(totalData)} entries
                    </>
                )}
            </div>

            <Pagination className="flex w-full items-center gap-8 lg:w-fit cursor-pointer">
                <PaginationContent>
                    <div className="flex w-fit items-center justify-center text-sm font-medium">
                        Page {pageIndex + 1} of {totalPages}
                    </div>

                    <Button
                        variant="outline"
                        className="hidden h-8 w-8 p-0 lg:flex"
                        onClick={() => handlePageChange(0)}
                        disabled={pageIndex === 0}
                    >
                        <span className="sr-only">Go to first page</span>
                        <ChevronsLeftIcon size={ICON_SIZE_1} />
                    </Button>
                    <Button
                        variant="outline"
                        className="size-8"
                        size="icon"
                        onClick={() => handlePageChange(pageIndex - 1)}
                        disabled={pageIndex === 0}
                    >
                        <span className="sr-only">Go to previous page</span>
                        <ChevronLeftIcon size={ICON_SIZE_1} />
                    </Button>

                    {showPages().map((items, i) => {
                        if (items < 0) {
                            return (
                                <PaginationItem key={`ellipsis-${i}`}>
                                    <PaginationEllipsis />
                                </PaginationItem>
                            )
                        }

                        return (
                            <PaginationItem key={items}>
                                <PaginationLink
                                    onClick={() => handlePageChange(items)}
                                    isActive={pageIndex === i}
                                >
                                    {items + 1}
                                </PaginationLink>
                            </PaginationItem>
                        )
                    })}

                    <Button
                        variant="outline"
                        className="size-8"
                        size="icon"
                        onClick={() => handlePageChange(pageIndex + 1)}
                        disabled={pageIndex >= totalPages - 1}
                    >
                        <span className="sr-only">Go to next page</span>
                        <ChevronRightIcon size={ICON_SIZE_1} />
                    </Button>
                    <Button
                        variant="outline"
                        className="hidden size-8 lg:flex"
                        size="icon"
                        onClick={() => handlePageChange(totalPages - 1)}
                        disabled={pageIndex === totalPages - 1}
                    >
                        <span className="sr-only">Go to last page</span>
                        <ChevronsRightIcon size={ICON_SIZE_1} />
                    </Button>
                </PaginationContent>
            </Pagination>
        </>
    )
}
